import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { HRC, SubTabs } from './hr/hrkit';
import HrDashboard from './hr/HrDashboard';
import EmployeeMaster from './hr/EmployeeMaster';
import AttendanceGrid from './hr/AttendanceGrid';
import Exceptions from './hr/Exceptions';
import LeaveAdmin from './hr/LeaveAdmin';
import LoansAdmin from './hr/LoansAdmin';
import Payroll from './hr/Payroll';
import Appraisals from './hr/Appraisals';
import Recruitment from './hr/Recruitment';
import Checklists from './hr/Checklists';
import SopCalendar from './hr/SopCalendar';
import MasterTaskSheet from './hr/MasterTaskSheet';
import { LettersLog } from './hr/Letters';
import AssessmentReview from './hr/AssessmentReview';
import TrainingAdmin from './hr/TrainingAdmin';
import FeedHealth from './hr/FeedHealth';
import MyHR from './hr/MyHR';

const HR_ROLES = ['admin', 'management', 'hr'];

export default function HR({ selfService }) {
  const { user } = useAuth();
  const isHr = HR_ROLES.includes(user?.role) && !selfService;
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState('dashboard');
  const [pending, setPending] = useState({ leave: 0, corr: 0, exceptions: 0 });
  const [feedAlerts, setFeedAlerts] = useState(0);
  // Cross-tab nav: open a specific employee's joining checklist (set after a hire).
  const [checklistTarget, setChecklistTarget] = useState(null);
  // Multi-tenant: the active company scopes every HR screen. The selected id is
  // persisted in localStorage and attached as X-Company-Id by the api interceptor.
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(localStorage.getItem('hrCompanyId') || '');
  const [companyReady, setCompanyReady] = useState(false);

  useEffect(() => {
    if (!isHr) { setCompanyReady(true); return; }
    api.get('/hr/companies').then(r => {
      const list = r.data.data || [];
      setCompanies(list);
      let active = localStorage.getItem('hrCompanyId');
      if (!r.data.can_switch || !active || !list.find(c => c.id === active)) {
        active = r.data.active || (list[0] && list[0].id) || '';
      }
      if (active) localStorage.setItem('hrCompanyId', active);
      setCompany(active || '');
      setCompanyReady(true);
    }).catch(() => setCompanyReady(true));
  }, [isHr]);

  const loadBadges = useCallback(() => {
    if (!isHr) return;
    api.get('/hr/dashboard').then(r => {
      const d = r.data.data; const ex = d.today_exceptions || {};
      setPending({
        leave: d.pending_leave || 0,
        corr: d.pending_corrections || 0,
        exceptions: (+ex.late || 0) + (+ex.miss || 0) + (+ex.early || 0) + (+ex.absent || 0),
      });
    }).catch(() => {});
    // Feed health drives its own badge: a silent feed counts as one alert, plus one per date the
    // feed refused and that has not since recovered. The point is that a broken attendance feed
    // is visible from the HR tab strip without anyone opening a screen to look for it.
    api.get('/hr/attendance/feed-health').then(r => {
      const h = r.data?.data || {};
      setFeedAlerts((h.is_stale ? 1 : 0) + Number(h.open_quarantine?.n || 0));
    }).catch(() => setFeedAlerts(0));
  }, [isHr]);
  useEffect(() => { if (companyReady) loadBadges(); }, [loadBadges, companyReady, company]);

  const changeCompany = (id) => {
    localStorage.setItem('hrCompanyId', id);
    setCompany(id);   // key change below remounts every screen → refetch under new tenant
  };
  const activeCompany = companies.find(c => c.id === company);

  // Employees with a linked portal login but no HR role → self-service only.
  if (!isHr) {
    return (
      <div style={{ padding: 24, background: HRC.pageBg, minHeight: '100%' }}>
        <Header subtitle="Self-service portal" />
        <MyHR />
      </div>
    );
  }

  const tabs = [
    { key: 'dashboard', label: '📊 Dashboard' },
    { key: 'recruitment', label: '🧑‍💼 Recruitment' },
    { key: 'checklists', label: '📋 Onboarding Checklist' },
    { key: 'sop', label: '📅 SOP Calendar' },
    { key: 'tasksheet', label: '📝 Master Task Sheet' },
    { key: 'letters', label: '📄 Letters' },
    { key: 'assessment', label: '📋 Assessment Review' },
    { key: 'training', label: '🎬 Training Videos' },
    { key: 'employees', label: '👥 Employees' },
    { key: 'attendance', label: '🗓 Attendance' },
    { key: 'feed', label: '📡 Feed & Roster', badge: feedAlerts },
    { key: 'exceptions', label: '⚠ Daily Exceptions', badge: pending.exceptions },
    { key: 'leave', label: '🌴 Leave', badge: pending.leave },
    { key: 'loans', label: '💸 Loans & Advances' },
    { key: 'payroll', label: '🧾 Payroll' },
    { key: 'appraisals', label: '⭐ Appraisals' },
    { key: 'corrections', label: '✋ Corrections', badge: pending.corr },
    { key: 'my', label: '🙋 My HR' },
  ];

  const openChecklist = (employeeId) => { setChecklistTarget(employeeId); setTab('checklists'); };
  const shared = { month, setMonth, onChange: loadBadges, goTo: setTab, openChecklist };

  return (
    <div style={{ padding: 24, background: HRC.pageBg, minHeight: '100%' }}>
      <Header
        subtitle={activeCompany ? activeCompany.name : 'Loading…'}
        company={company} companies={companies} onCompany={changeCompany} />
      <SubTabs tabs={tabs} active={tab} onChange={setTab} />
      {!companyReady ? (
        <div style={{ padding: 40, color: HRC.muted }}>Loading…</div>
      ) : (
        // key on company → switching tenants remounts the active screen so it
        // refetches everything under the newly-selected company_id.
        <div key={company}>
          {tab === 'dashboard' && <HrDashboard {...shared} />}
          {tab === 'recruitment' && <Recruitment {...shared} companyCode={activeCompany?.code} />}
          {tab === 'checklists' && <Checklists {...shared} target={checklistTarget} clearTarget={() => setChecklistTarget(null)} />}
          {tab === 'sop' && <SopCalendar />}
          {tab === 'tasksheet' && <MasterTaskSheet />}
          {tab === 'letters' && <LettersLog />}
          {tab === 'assessment' && <AssessmentReview />}
          {tab === 'training' && <TrainingAdmin />}
          {tab === 'employees' && <EmployeeMaster {...shared} />}
          {tab === 'attendance' && <AttendanceGrid {...shared} />}
          {tab === 'feed' && <FeedHealth />}
          {tab === 'exceptions' && <Exceptions {...shared} mode="exceptions" />}
          {tab === 'corrections' && <Exceptions {...shared} mode="corrections" />}
          {tab === 'leave' && <LeaveAdmin {...shared} />}
          {tab === 'loans' && <LoansAdmin {...shared} />}
          {tab === 'payroll' && <Payroll {...shared} />}
          {tab === 'appraisals' && <Appraisals {...shared} />}
          {tab === 'my' && <MyHR />}
        </div>
      )}
    </div>
  );
}

function Header({ subtitle, company, companies, onCompany }) {
  const canSwitch = (companies || []).length > 1;
  return (
    <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 22, color: HRC.sidebar, fontWeight: 800 }}>Human Resources</h1>
        <div style={{ fontSize: 12, color: HRC.muted, marginTop: 2 }}>{subtitle}</div>
      </div>
      {canSwitch && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Company</span>
          <select value={company} onChange={e => onCompany(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${HRC.border}`, fontSize: 13, fontWeight: 700,
              color: HRC.sidebar, background: HRC.violet, cursor: 'pointer' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
